// #354: one spelling per role.
//
// `bg-brand-600` was written 16 different ways across 26 files. They disagreed
// on weight (`font-medium` vs `font-semibold`), padding (`px-3`/`px-4`/`px-5`),
// whether there was a `transition`, whether there was an `active:` state, and
// what disabled looked like (`opacity-50` vs `opacity-60`). That is visible,
// not cosmetic: the primary button on /book was a different weight and width
// from the one on /signin from the one in NotFound.
//
// This is a CONSOLIDATION, not a redesign. For each role the most common
// existing spelling wins, so the app looks the same afterwards except where it
// was already inconsistent. Where the two variants were evenly split the more
// deliberate one wins — `font-semibold` for a primary action (14 sites to 11),
// and `transition` always, because a button that animates on one page and
// snaps on the next reads as a bug.
//
// Tailwind scans `src/**/*.js`, so these full strings are seen by the compiler.
// Never build one of these by concatenating fragments — Tailwind cannot see a
// class that only exists at runtime.

// The three legitimate target sizes.
//
//   PAGE    44px — the default for anything a page presents as an action.
//   DENSE   36px — controls inside a bar that is itself only a little taller:
//                  the header nav, the filter chip row. Forcing 44px here does
//                  not make them easier to hit, it makes the bar grow.
//   COMPACT 32px — the in-card action bar (#350), where a card can carry
//                  several rows of actions and 44px turns a three-talk
//                  conference into a wall of buttons.
//
// The 40px instances were drift and are gone. If you reach for a fourth size,
// the answer is almost always one of these three.
export const TARGET_PAGE = 'min-h-[44px]'
export const TARGET_DENSE = 'min-h-[36px]'
export const TARGET_COMPACT = 'min-h-[32px]'

const BASE = 'inline-flex items-center justify-center rounded-full transition'

// The filled brand action. One per view, ideally.
export const BTN_PRIMARY =
  `${BASE} ${TARGET_PAGE} bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 active:bg-brand-800 disabled:opacity-50`

// Same, stretched — the submit button at the foot of a form column.
export const BTN_PRIMARY_BLOCK = `${BTN_PRIMARY} w-full`

// The header's sign-in button. 36px is deliberate and the ONLY sanctioned
// exception to TARGET_PAGE: the nav bar is not tall enough to seat a 44px
// control without the header growing around it.
export const BTN_PRIMARY_SM =
  `${BASE} min-h-[36px] bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 active:bg-brand-800 disabled:opacity-50`

// The outlined alternative that sits beside a primary.
export const BTN_SECONDARY =
  `${BASE} ${TARGET_PAGE} px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50`

// No fill, no ring — a tertiary action in a row of them.
export const BTN_GHOST =
  `${BASE} ${TARGET_PAGE} px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50`

// A ghost that reads as a link into brand territory (Download .ics, and kin).
export const BTN_GHOST_BRAND =
  `${BASE} ${TARGET_PAGE} px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50`

// A destructive action. Same shape as secondary so it never looks primary.
export const BTN_DANGER =
  `${BASE} ${TARGET_PAGE} px-4 py-2 text-sm font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50 disabled:opacity-50`

// --- compact, for the in-card action bar (#350) ------------------------------

const COMPACT = `inline-flex ${TARGET_COMPACT} items-center rounded-full px-2.5 py-1 text-xs font-medium transition`

export const BTN_COMPACT_PRIMARY =
  `${COMPACT} bg-brand-600 px-3 text-white shadow-sm hover:bg-brand-700 active:bg-brand-800`
export const BTN_COMPACT_BRAND = `${COMPACT} text-brand-700 hover:bg-brand-50`
export const BTN_COMPACT_GHOST = `${COMPACT} text-slate-600 hover:bg-slate-100`
export const BTN_COMPACT_ICON = `${BTN_COMPACT_GHOST} min-w-[32px] justify-center`

// --- non-interactive labels --------------------------------------------------

export const PILL_BRAND =
  'inline-flex items-center rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-800'
export const PILL_NEUTRAL =
  'inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-700'

// --- forms -------------------------------------------------------------------

// `const inputClass = ...` was redefined in EIGHT files. They were in fact only
// two strings — this one, and this one with `mt-1 w-full` in front — so the
// duplication bought nothing at all.
export const INPUT =
  'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:cursor-not-allowed disabled:bg-slate-100'

// The same input as a full-width field under its own label.
export const INPUT_BLOCK = `mt-1 w-full ${INPUT}`

export const LABEL = 'block text-sm font-medium text-slate-700'

// A ghost sized for a table row or a list item, where 44px would space the rows
// out more than the content wants.
export const BTN_ROW =
  `${BASE} ${TARGET_PAGE} px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50`
export const BTN_ROW_DANGER =
  `${BASE} ${TARGET_PAGE} px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50`
